import { useState, useEffect, useMemo, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Minus, Trash2, Search, UserPlus, User, Printer, Settings, X } from 'lucide-react';
import ThermalTicket from '../components/ThermalTicket';

const API = process.env.REACT_APP_BACKEND_URL;
const SHORTCUT_COUNT = 10;
const BRAND_BLUE = '#1D3C6E';
const BRAND_YELLOW = '#AFBD20';

export default function POS() {
  const { getAuthHeaders, currentBranch, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [inventory, setInventory] = useState([]);
  const [shortcuts, setShortcuts] = useState(Array(SHORTCUT_COUNT).fill(''));
  const [cart, setCart] = useState([]);
  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showResults, setShowResults] = useState(false);
  const searchBoxRef = useRef(null);
  const [payments, setPayments] = useState([{ method: 'Efectivo', amount: '' }]);
  const [clientName, setClientName] = useState('');
  const [clientId, setClientId] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [clientResults, setClientResults] = useState([]);
  const [clientNotFound, setClientNotFound] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);
  const [clientForm, setClientForm] = useState({ name: '', phone: '', phone2: '', email: '' });
  const [processing, setProcessing] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [assignSlot, setAssignSlot] = useState(-1);
  const [assignSearch, setAssignSearch] = useState('');
  const [saveCredit, setSaveCredit] = useState(false);

  useEffect(() => { fetchInventory(); fetchShortcuts(); }, []);

  const fetchInventory = async () => {
    try {
      const res = await axios.get(`${API}/api/inventory`, { headers: getAuthHeaders() });
      setInventory((res.data || []).filter(i => {
        if (i.active === false) return false;
        const cat = String(i.category || i.type || '').toLowerCase();
        return cat !== 'service' && cat !== 'servicio' && cat !== 'servicios';
      }));
    } catch (err) { console.error(err); }
  };

  const fetchShortcuts = async () => {
    try {
      const res = await axios.get(`${API}/api/settings/pos_shortcuts`, { headers: getAuthHeaders() });
      const list = res.data?.value;
      if (Array.isArray(list)) {
        const arr = Array(SHORTCUT_COUNT).fill('');
        list.slice(0, SHORTCUT_COUNT).forEach((v, i) => { arr[i] = v || ''; });
        setShortcuts(arr);
      }
    } catch (err) { /* first-time */ }
  };

  const saveShortcuts = async (next) => {
    setShortcuts(next);
    try {
      await axios.put(`${API}/api/settings/pos_shortcuts`, { value: next }, { headers: getAuthHeaders() });
    } catch (err) { alert(err.response?.data?.detail || 'No se pudo guardar'); fetchShortcuts(); }
  };

  const branchStock = (item) => {
    if (!item) return 0;
    if (currentBranch && item.stock_by_branch && item.stock_by_branch[currentBranch] !== undefined) {
      return Number(item.stock_by_branch[currentBranch] || 0);
    }
    return Number(item.stock || 0);
  };

  const shortcutItems = useMemo(() => shortcuts.map(id => id ? inventory.find(i => i.id === id) : null), [shortcuts, inventory]);

  // Client search
  const searchClient = async (query) => {
    setClientSearch(query);
    setClientNotFound(false);
    if (!query.trim()) { setClientResults([]); return; }
    try {
      const res = await axios.get(`${API}/api/clients?search=${encodeURIComponent(query)}`, { headers: getAuthHeaders() });
      setClientResults(res.data);
      if (res.data.length === 0 && query.trim().length >= 3) setClientNotFound(true);
    } catch (err) { console.error(err); }
  };
  const selectClient = (c) => { setClientName(c.name); setClientId(c.id); setClientSearch(c.phone + ' - ' + c.name); setClientResults([]); setClientNotFound(false); };
  const clearClient = () => { setClientName(''); setClientId(''); setClientSearch(''); setClientResults([]); setClientNotFound(false); };
  const handleRegisterClient = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${API}/api/clients`, clientForm, { headers: getAuthHeaders() });
      selectClient(res.data);
      setShowClientForm(false);
      setClientForm({ name: '', phone: '', phone2: '', email: '' });
    } catch (err) { console.error(err); }
  };

  const filteredItems = useMemo(() => {
    if (!search.trim()) return [];
    return searchResults;
  }, [search, searchResults]);

  // Debounced backend search
  useEffect(() => {
    const q = search.trim();
    if (!q) { setSearchResults([]); setShowResults(false); return; }
    setShowResults(true);
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/api/inventory/pos-search?q=${encodeURIComponent(q)}`, { headers: getAuthHeaders() });
        setSearchResults(res.data || []);
      } catch (err) { setSearchResults([]); }
    }, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  // Close on outside click / Escape
  useEffect(() => {
    if (!showResults) return;
    const onDown = (e) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target)) setShowResults(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setShowResults(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [showResults]);

  // Cart operations
  const addToCart = (item) => {
    if (!item) return;
    const stock = branchStock(item);
    if (stock <= 0) return;
    const existing = cart.find(c => c.inventory_id === item.id);
    if (existing) {
      if (existing.quantity >= stock) return;
      setCart(cart.map(c => c.inventory_id === item.id ? { ...c, quantity: c.quantity + 1 } : c));
    } else {
      setCart([...cart, {
        inventory_id: item.id,
        sku: item.sku || '',
        modelo: item.modelo || '',
        color: item.color || '',
        name: item.name || '',
        price: item.sale_price || 0,
        quantity: 1,
        type: item.category || item.type,
        max_stock: stock,
        discount_type: 'percentage',
        discount_value: 0,
      }]);
    }
  };
  const updateQuantity = (idx, delta) => {
    setCart(cart.map((it, i) => {
      if (i !== idx) return it;
      const nq = it.quantity + delta;
      if (nq < 1 || nq > it.max_stock) return it;
      return { ...it, quantity: nq };
    }));
  };
  const updateItemDiscount = (idx, field, value) => {
    setCart(cart.map((it, i) => {
      if (i !== idx) return it;
      if (field === 'discount_value') {
        const n = Math.max(0, Math.min(999, parseFloat(value) || 0));
        return { ...it, discount_value: n };
      }
      if (field === 'discount_type') {
        return { ...it, discount_type: value };
      }
      return it;
    }));
  };
  const removeFromCart = (idx) => setCart(cart.filter((_, i) => i !== idx));

  // Per-item discount math
  const lineGross = (it) => (it.price || 0) * (it.quantity || 0);
  const lineDiscount = (it) => {
    const gross = lineGross(it);
    const v = parseFloat(it.discount_value) || 0;
    if (it.discount_type === 'percentage') return Math.max(0, Math.min(gross, gross * (v / 100)));
    if (it.discount_type === 'fixed') return Math.max(0, Math.min(gross, v));
    return 0;
  };
  const lineSubtotal = (it) => lineGross(it) - lineDiscount(it);

  const subtotal = cart.reduce((s, it) => s + lineGross(it), 0);
  const discountAmount = cart.reduce((s, it) => s + lineDiscount(it), 0);
  const total = Math.max(0, subtotal - discountAmount);
  const totalPaid = payments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
  const remaining = Math.max(0, total - totalPaid);
  const change = Math.max(0, totalPaid - total);

  const addPaymentLine = () => setPayments([...payments, { method: 'Efectivo', amount: '' }]);
  const updatePayment = (i, field, val) => setPayments(payments.map((p, ix) => ix === i ? { ...p, [field]: val } : p));
  const removePayment = (i) => setPayments(payments.length === 1 ? [{ method: 'Efectivo', amount: '' }] : payments.filter((_, ix) => ix !== i));

  const handleSale = async () => {
    if (cart.length === 0) return;
    if (remaining > 0.01 && !clientId && saveCredit) { alert('Selecciona un cliente para guardar el saldo como crédito.'); return; }
    setProcessing(true);
    try {
      const payload = {
        items: cart.map(it => ({
          inventory_id: it.inventory_id,
          name: it.name,
          sku: it.sku,
          modelo: it.modelo,
          color: it.color,
          price: it.price,
          quantity: it.quantity,
          type: it.type,
          discount_type: it.discount_type,
          discount_value: parseFloat(it.discount_value) || 0,
        })),
        discount_type: 'none',
        discount_value: 0,
        payments: payments.filter(p => (parseFloat(p.amount) || 0) > 0).map(p => ({ method: p.method, amount: parseFloat(p.amount) })),
        client_id: clientId || '',
        client_name: clientName || 'Venta general',
        branch_id: currentBranch || '',
      };
      const res = await axios.post(`${API}/api/pos/sale`, payload, { headers: getAuthHeaders() });
      setLastSale(res.data);
      setCart([]);
      setPayments([{ method: 'Efectivo', amount: '' }]);
      setSaveCredit(false);
      clearClient();
      fetchInventory();
    } catch (err) { alert(err.response?.data?.detail || 'Error al procesar venta'); }
    setProcessing(false);
  };

  const assignShortcut = (item) => {
    if (assignSlot < 0) return;
    const next = [...shortcuts];
    next[assignSlot] = item.id;
    saveShortcuts(next);
    setAssignSlot(-1); setAssignSearch('');
  };
  const clearShortcut = (i) => { const next = [...shortcuts]; next[i] = ''; saveShortcuts(next); };
  const assignInventoryList = useMemo(() => {
    const norm = (s) => (s || '').toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    const words = norm(assignSearch).split(/\s+/).filter(Boolean);
    return inventory.filter(i => {
      if (!words.length) return true;
      const searchable = `${norm(i.name)} ${norm(i.modelo)} ${norm(i.sku)}`;
      return words.every(w => searchable.includes(w));
    }).slice(0, 20);
  }, [assignSearch, inventory]);

  return (
    <div
      data-testid="pos-page"
      className="h-[calc(100dvh-6.5rem)] flex flex-col gap-2 overflow-hidden"
    >
      {/* 1) SEARCH BLOCK */}
      <div className="bg-white rounded-lg border border-slate-200 p-3 flex-shrink-0 relative z-20" ref={searchBoxRef}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 z-10" />
          <Input
            data-testid="pos-search"
            className="pl-11 h-11 text-base"
            placeholder="Buscar por SKU, modelo, nombre, color, descripción..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onFocus={() => { if (search.trim()) setShowResults(true); }}
          />
          {search.trim() && showResults && (
            <div
              className="absolute left-0 right-0 top-full mt-1 z-30 bg-white border border-slate-200 rounded-lg shadow-lg max-h-[60vh] overflow-y-auto"
              data-testid="pos-search-dropdown"
            >
              {filteredItems.length === 0 ? (
                <div className="px-4 py-6 text-center text-slate-400 text-sm">Sin resultados</div>
              ) : filteredItems.map(item => {
                const stock = branchStock(item);
                const disabled = stock <= 0;
                return (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => { addToCart(item); setShowResults(false); }}
                    disabled={disabled}
                    className={`w-full text-left px-4 py-2.5 border-b border-slate-100 last:border-b-0 flex items-center gap-3 text-sm ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-[#AFBD20]/10 cursor-pointer'}`}
                    data-testid={`pos-item-${item.id}`}
                  >
                    <span className="font-mono text-xs text-slate-500 w-20 shrink-0 truncate">{item.sku || '—'}</span>
                    <span className="text-slate-700 w-28 shrink-0 truncate">{item.modelo || '—'}</span>
                    <span className="font-medium text-slate-900 flex-1 truncate">{item.name || '—'}</span>
                    <span className="text-slate-600 w-24 shrink-0 truncate">{item.color || '—'}</span>
                    <span className="text-slate-500 flex-1 truncate">{item.description || '—'}</span>
                    <span className="text-xs font-bold text-[#1D3C6E] w-20 text-right shrink-0">${(item.sale_price || 0).toFixed(2)}</span>
                    <span className={`text-xs w-16 text-right shrink-0 ${disabled ? 'text-red-500 font-semibold' : 'text-slate-400'}`}>Stock: {stock}</span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* 2) CART BLOCK */}
      <div className="bg-white rounded-lg border border-slate-200 flex-1 min-h-0 flex flex-col overflow-hidden">
        {/* Table header */}
        <div className="grid grid-cols-[minmax(0,1fr)_120px_180px_120px_44px] gap-3 px-4 py-2 border-b border-slate-200 bg-slate-50 text-xs font-semibold text-slate-600 uppercase tracking-wide flex-shrink-0">
          <div>Artículo</div>
          <div className="text-center">Cant.</div>
          <div className="text-center">Desc.</div>
          <div className="text-right">Subtotal</div>
          <div></div>
        </div>

        {/* Rows */}
        {cart.length === 0 ? (
          <div className="flex-1 flex items-center justify-center text-slate-400 text-base">
            Carrito vacío
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-y-auto" data-testid="pos-cart-items">
            {cart.map((it, idx) => (
              <div
                key={idx}
                className="grid grid-cols-[minmax(0,1fr)_120px_180px_120px_44px] gap-3 px-4 py-3 border-b border-slate-100 items-center"
                data-testid={`cart-row-${idx}`}
              >
                {/* Artículo */}
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
                    <span className="font-mono">{it.sku || '—'}</span>
                    <span className="text-slate-300">·</span>
                    <span>{it.modelo || '—'}</span>
                    <span className="text-slate-300">·</span>
                    <span>{it.color || '—'}</span>
                  </div>
                  <p
                    className="text-base font-semibold text-slate-900 leading-snug break-words whitespace-normal"
                    data-testid={`cart-name-${idx}`}
                  >{it.name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">${(it.price || 0).toFixed(2)} c/u</p>
                </div>

                {/* Cantidad */}
                <div className="flex items-center justify-center gap-1">
                  <button
                    type="button"
                    onClick={() => updateQuantity(idx, -1)}
                    className="w-8 h-8 rounded bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
                    data-testid={`cart-qty-minus-${idx}`}
                  ><Minus className="w-4 h-4" /></button>
                  <span className="w-8 text-center font-bold text-base" data-testid={`cart-qty-${idx}`}>{it.quantity}</span>
                  <button
                    type="button"
                    onClick={() => updateQuantity(idx, 1)}
                    className="w-8 h-8 rounded bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
                    data-testid={`cart-qty-plus-${idx}`}
                  ><Plus className="w-4 h-4" /></button>
                </div>

                {/* Descuento por item */}
                <div className="flex items-center justify-center gap-1">
                  <Input
                    type="number"
                    min="0"
                    max="999"
                    step="0.01"
                    value={it.discount_value || ''}
                    onChange={(e) => updateItemDiscount(idx, 'discount_value', e.target.value)}
                    placeholder="0"
                    className="w-16 h-8 text-sm text-center"
                    data-testid={`cart-disc-value-${idx}`}
                  />
                  <div className="inline-flex rounded-md border border-slate-200 overflow-hidden">
                    <button
                      type="button"
                      onClick={() => updateItemDiscount(idx, 'discount_type', 'percentage')}
                      className={`px-2 h-8 text-xs font-semibold ${it.discount_type === 'percentage' ? 'bg-[#1D3C6E] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                      data-testid={`cart-disc-pct-${idx}`}
                    >%</button>
                    <button
                      type="button"
                      onClick={() => updateItemDiscount(idx, 'discount_type', 'fixed')}
                      className={`px-2 h-8 text-xs font-semibold border-l border-slate-200 ${it.discount_type === 'fixed' ? 'bg-[#1D3C6E] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`}
                      data-testid={`cart-disc-fixed-${idx}`}
                    >$</button>
                  </div>
                </div>

                {/* Subtotal */}
                <div className="text-right">
                  <div className="text-base font-bold text-slate-900" data-testid={`cart-subtotal-${idx}`}>${lineSubtotal(it).toFixed(2)}</div>
                  {lineDiscount(it) > 0 && (
                    <div className="text-[11px] text-red-500">-${lineDiscount(it).toFixed(2)}</div>
                  )}
                </div>

                {/* Delete */}
                <button
                  type="button"
                  onClick={() => removeFromCart(idx)}
                  className="w-9 h-9 rounded bg-red-50 hover:bg-red-100 flex items-center justify-center justify-self-end"
                  data-testid={`cart-remove-${idx}`}
                  title="Quitar"
                ><Trash2 className="w-4 h-4 text-red-500" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 3) MONEY BLOCK — two columns */}
      <div className="bg-white rounded-lg border border-slate-200 p-3 md:p-4 flex-shrink-0">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {/* Left column: Cliente + Pagos */}
          <div className="flex flex-col gap-3 min-w-0">
            {/* Cliente */}
            <div>
              <label className="text-xs font-semibold text-slate-600 mb-1 block">Cliente</label>
              {clientId ? (
                <div className="flex items-center gap-2 p-2 bg-blue-50 border border-blue-200 rounded-lg h-10">
                  <User className="w-4 h-4 text-[#1D3C6E]" />
                  <span className="text-sm font-medium flex-1 truncate">{clientName}</span>
                  <button onClick={clearClient} className="text-xs text-red-500 hover:text-red-700" data-testid="pos-clear-client">Cambiar</button>
                </div>
              ) : (
                <div className="relative">
                  <Input
                    className="h-10 text-sm"
                    placeholder="Buscar por nombre o teléfono..."
                    value={clientSearch}
                    onChange={(e) => searchClient(e.target.value)}
                    data-testid="pos-client-search"
                  />
                  {clientResults.length > 0 && (
                    <div className="absolute z-20 left-0 right-0 mt-1 bg-white border border-slate-200 rounded-lg shadow-md max-h-40 overflow-y-auto" data-testid="pos-client-results">
                      {clientResults.map(c => (
                        <div key={c.id} onClick={() => selectClient(c)} className="px-3 py-2 hover:bg-slate-50 cursor-pointer border-b border-slate-100 last:border-0">
                          <p className="text-sm font-medium">{c.name}</p>
                          <p className="text-xs text-slate-500">{c.phone}</p>
                        </div>
                      ))}
                    </div>
                  )}
                  {clientNotFound && (
                    <div className="mt-1 p-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center justify-between" data-testid="pos-client-not-found">
                      <span className="text-xs text-amber-700">Cliente no registrado</span>
                      <button onClick={() => {
                        const isPhone = /^\d+$/.test(clientSearch.trim());
                        setClientForm({ name: isPhone ? '' : clientSearch.trim(), phone: isPhone ? clientSearch.trim() : '', phone2: '', email: '' });
                        setShowClientForm(true);
                      }} className="text-xs font-medium text-[#1D3C6E] hover:underline flex items-center gap-1" data-testid="pos-register-client-btn">
                        <UserPlus className="w-3.5 h-3.5" /> Registrar
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Pagos */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-semibold text-slate-600">Pagos</label>
                <button type="button" onClick={addPaymentLine} className="text-xs font-medium text-[#1D3C6E] hover:underline flex items-center gap-1" data-testid="add-payment-btn">
                  <Plus className="w-3.5 h-3.5" /> Agregar pago
                </button>
              </div>
              <div className="space-y-1.5">
                {payments.map((p, i) => (
                  <div key={i} className="flex gap-1.5" data-testid={`payment-line-${i}`}>
                    <Select value={p.method} onValueChange={(v) => updatePayment(i, 'method', v)}>
                      <SelectTrigger className="h-9 text-sm w-36"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Efectivo">Efectivo</SelectItem>
                        <SelectItem value="Tarjeta">Tarjeta</SelectItem>
                        <SelectItem value="Transferencia">Transferencia</SelectItem>
                      </SelectContent>
                    </Select>
                    <Input
                      className="h-9 text-sm flex-1"
                      type="number"
                      placeholder="Monto"
                      value={p.amount}
                      onChange={(e) => updatePayment(i, 'amount', e.target.value)}
                      data-testid={`payment-amount-${i}`}
                    />
                    <button type="button" onClick={() => removePayment(i)} className="w-9 h-9 rounded bg-red-50 hover:bg-red-100 flex items-center justify-center shrink-0" title="Quitar">
                      <Trash2 className="w-3.5 h-3.5 text-red-500" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right column: Totales + Completar Venta */}
          <div className="flex flex-col gap-2 min-w-0">
            <div className="flex justify-between items-baseline text-sm">
              <span className="text-slate-500">Subtotal</span>
              <span className="font-semibold text-slate-700" data-testid="pos-subtotal">${subtotal.toFixed(2)}</span>
            </div>
            <div className="flex justify-between items-baseline text-sm">
              <span className="text-slate-500">Descuentos</span>
              <span className={`font-semibold ${discountAmount > 0 ? 'text-red-500' : 'text-slate-700'}`} data-testid="pos-discounts">
                {discountAmount > 0 ? `-$${discountAmount.toFixed(2)}` : '$0.00'}
              </span>
            </div>
            <div className="border-t border-slate-200 pt-2 flex justify-between items-baseline">
              <span className="text-xl font-bold text-slate-900">Total</span>
              <span className="text-3xl md:text-4xl font-bold" style={{ color: BRAND_BLUE }} data-testid="pos-total">${total.toFixed(2)}</span>
            </div>
            <div className="border-t border-slate-200 pt-2 flex flex-col gap-1">
              <div className="flex justify-between items-baseline">
                <span className="text-lg font-bold text-emerald-700">Pagado</span>
                <span className="text-2xl md:text-3xl font-bold text-emerald-600" data-testid="pos-paid">${totalPaid.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-lg font-bold text-red-700">Restante</span>
                <span className="text-2xl md:text-3xl font-bold text-red-600" data-testid="pos-remaining">${remaining.toFixed(2)}</span>
              </div>
              <div className="flex justify-between items-baseline">
                <span className="text-lg font-bold" style={{ color: BRAND_YELLOW }}>Feria</span>
                <span className="text-2xl md:text-3xl font-bold" style={{ color: BRAND_YELLOW }} data-testid="pos-feria">${change.toFixed(2)}</span>
              </div>
            </div>

            {remaining > 0.01 && (
              <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer" data-testid="save-credit-toggle">
                <input type="checkbox" checked={saveCredit} onChange={(e) => setSaveCredit(e.target.checked)} className="rounded" />
                Guardar saldo restante como crédito del cliente
              </label>
            )}

            {remaining > 0.01 && !saveCredit && (
              <p className="text-sm font-semibold text-red-600" data-testid="pos-remaining-warning">
                Falta por pagar: ${remaining.toFixed(2)}
              </p>
            )}

            <Button
              onClick={handleSale}
              disabled={cart.length === 0 || processing || (remaining > 0.01 && !saveCredit)}
              className="w-full h-12 text-base font-bold hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: BRAND_YELLOW, color: BRAND_BLUE }}
              data-testid="pos-complete-sale"
            >
              {processing ? 'Procesando...' : 'Completar Venta'}
            </Button>

            {lastSale && (
              <div className="p-2 bg-green-50 border border-green-200 rounded-lg text-xs flex items-center gap-2">
                <p className="font-medium text-green-700 flex-1 truncate">Venta completada — {lastSale.ticket} — ${lastSale.total?.toFixed(2)}</p>
                <button onClick={() => window.print()} className="flex items-center gap-1 px-2 py-1 rounded bg-white border border-green-300 text-green-700 hover:bg-green-100 font-medium" data-testid="pos-print-ticket-btn">
                  <Printer className="w-3 h-3" /> Ticket
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 4) SHORTCUTS BLOCK */}
      <div className="bg-white rounded-lg border border-slate-200 p-2 flex-shrink-0">
        <div className="grid grid-cols-10 gap-2" data-testid="pos-shortcuts-grid">
          {shortcutItems.map((it, i) => {
            if (!it) {
              return (
                <button
                  key={i}
                  type="button"
                  onClick={() => isAdmin && setAssignSlot(i)}
                  disabled={!isAdmin}
                  className={`h-16 rounded-lg border-2 border-dashed flex flex-col items-center justify-center transition-colors ${isAdmin ? 'border-slate-300 hover:border-[#AFBD20] hover:bg-[#AFBD20]/5 text-slate-400 hover:text-[#AFBD20] cursor-pointer' : 'border-slate-200 text-slate-300 cursor-not-allowed'}`}
                  data-testid={`shortcut-slot-empty-${i}`}
                  title={isAdmin ? 'Asignar artículo' : 'Solo admin puede configurar'}
                >
                  <Plus className="w-4 h-4" />
                  <span className="text-[10px] mt-0.5">Vacío</span>
                </button>
              );
            }
            const stock = branchStock(it);
            return (
              <div key={i} className="relative group">
                <button
                  type="button"
                  onClick={() => addToCart(it)}
                  className={`w-full h-16 rounded-lg border-2 bg-white p-1.5 flex flex-col text-left transition-all ${stock <= 0 ? 'border-red-200 opacity-70' : 'border-slate-200 hover:border-[#AFBD20] hover:shadow-md'}`}
                  data-testid={`shortcut-slot-${i}`}
                >
                  <p className="text-[10px] font-semibold text-slate-800 leading-tight break-words line-clamp-2">{it.name}</p>
                  <div className="mt-auto pt-0.5">
                    <div className="text-xs font-bold text-[#1D3C6E]">${(it.sale_price || 0).toFixed(2)}</div>
                    <div className={`text-[9px] ${stock <= 0 ? 'text-red-500 font-semibold' : 'text-slate-500'}`}>Stock: {stock}</div>
                  </div>
                </button>
                {isAdmin && (
                  <div className="absolute top-0.5 right-0.5 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button type="button" onClick={() => setAssignSlot(i)} className="w-4 h-4 rounded bg-slate-800/80 text-white flex items-center justify-center" title="Cambiar" data-testid={`shortcut-edit-${i}`}>
                      <Settings className="w-2.5 h-2.5" />
                    </button>
                    <button type="button" onClick={() => clearShortcut(i)} className="w-4 h-4 rounded bg-red-500/80 text-white flex items-center justify-center" title="Vaciar" data-testid={`shortcut-clear-${i}`}>
                      <X className="w-2.5 h-2.5" />
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {lastSale && <ThermalTicket variant="sale" data={lastSale} branchName="" />}

      {/* Register client modal */}
      <Dialog open={showClientForm} onOpenChange={setShowClientForm}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-[#1D3C6E] font-['Outfit']">Registrar Cliente</DialogTitle></DialogHeader>
          <form onSubmit={handleRegisterClient} className="space-y-3">
            <div><label className="text-xs font-medium text-slate-600">Nombre *</label><Input data-testid="pos-new-client-name" value={clientForm.name} onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })} required /></div>
            <div><label className="text-xs font-medium text-slate-600">Teléfono *</label><Input data-testid="pos-new-client-phone" value={clientForm.phone} onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })} required /></div>
            <div><label className="text-xs font-medium text-slate-600">Email</label><Input type="email" value={clientForm.email} onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })} /></div>
            <Button type="submit" className="w-full bg-[#1D3C6E] hover:bg-[#152B50]" data-testid="pos-submit-new-client">Guardar y Seleccionar</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign shortcut modal */}
      <Dialog open={assignSlot >= 0} onOpenChange={(o) => { if (!o) { setAssignSlot(-1); setAssignSearch(''); } }}>
        <DialogContent className="max-w-lg" data-testid="assign-shortcut-dialog">
          <DialogHeader><DialogTitle className="text-[#1D3C6E] font-['Outfit']">Asignar acceso directo #{assignSlot + 1}</DialogTitle></DialogHeader>
          <div>
            <div className="relative mb-3">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input autoFocus className="pl-10" placeholder="Buscar por SKU, nombre, modelo..." value={assignSearch} onChange={(e) => setAssignSearch(e.target.value)} data-testid="assign-shortcut-search" />
            </div>
            <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-md">
              {assignInventoryList.map(it => (
                <button key={it.id} type="button" onClick={() => assignShortcut(it)} className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center gap-3 border-b border-slate-50 last:border-b-0" data-testid={`assign-option-${it.id}`}>
                  <span className="font-mono text-xs text-slate-500 w-16 shrink-0">{it.sku || '—'}</span>
                  <span className="flex-1 text-sm font-medium text-slate-800 truncate">{it.name}</span>
                  <span className="text-xs text-[#1D3C6E] font-semibold shrink-0">${(it.sale_price || 0).toFixed(2)}</span>
                </button>
              ))}
              {assignInventoryList.length === 0 && <div className="px-3 py-4 text-center text-xs text-slate-400">Sin resultados</div>}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
